import { FC, useEffect, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router-dom";
import { CALENDAR_SHIFT_DAY_ROUTE } from "../../../app/router/routes";
import { ILocalDate } from "../../../entities/calendar";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { IShiftCalendarDay, IStaffShiftPattern, shiftService, TShiftAction, TShiftStatus } from "../../../entities/shift";
import { StaffShiftPatternEdit } from "../../../features/staffShiftPatternEdit";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { useGetMonthWeeks } from "../../../shared/lib/hooks/useGetMonthWeeks";
import { LoaderSpinner } from "../../../shared/ui/spinner";
import classes from './staffShiftCalendar.module.scss'

interface IProps {
    userId: string;
    title?: string;
    hideTitle?: boolean;
}

const getLocalDateFromDate = (date: Date): ILocalDate => ({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
})

const getDateParam = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')

    return `${year}-${month}-${day}`
}

const getLocalDateLabel = (date?: ILocalDate): string => {
    if(!date || !date.year || !date.month || !date.day) return 'не указано'

    return `${String(date.day).padStart(2, '0')}.${String(date.month).padStart(2, '0')}.${date.year}`
}

const getSlotEndTimeLabel = (slotIndex: number): string => {
    if(slotIndex >= 48) return '24:00'
    return getSlotTimeLabel(slotIndex)
}

const getSlotTimeLabel = (slotIndex: number): string => {
    const minutes = slotIndex * 30
    const hours = Math.floor(minutes / 60)
    const restMinutes = minutes % 60

    return `${String(hours).padStart(2, '0')}:${String(restMinutes).padStart(2, '0')}`
}

const getStatusLabel = (status: TShiftStatus): string => {
    switch(status){
        case 'SHIFT_STATUS_PLANNED':
            return 'Запланировано'
        case 'SHIFT_STATUS_COMPLETED':
            return 'Завершено'
        case 'SHIFT_STATUS_CANCELLED':
            return 'Отменено'
        default:
            return 'Нет смены'
    }
}

export const StaffShiftCalendar: FC<IProps> = ({
    userId,
    title = 'Персональный график смен',
    hideTitle = false,
}) => {

    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()
    const navigate = useNavigate()

    const [visibleMonth, setVisibleMonth] = useState<Date>(new Date())
    const [calendarDays, setCalendarDays] = useState<IShiftCalendarDay[]>([])
    const [pattern, setPattern] = useState<IStaffShiftPattern | undefined>()
    const [availableActions, setAvailableActions] = useState<TShiftAction[]>([])
    const [isEditOpen, setIsEditOpen] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [reloadIndex, setReloadIndex] = useState<number>(0)

    const weeks = useGetMonthWeeks(visibleMonth)
    const monthTitle = visibleMonth
        .toLocaleString('ru-RU', {month: 'long', year: 'numeric'})
        .replace(/\s*г\.$/, '')
    const canUpdatePattern = availableActions.includes('SHIFT_ACTION_UPDATE_PATTERN')
    const patternTime = pattern
        ? `${getSlotTimeLabel(pattern.startSlot)} — ${getSlotEndTimeLabel(pattern.startSlot + pattern.durationSlots)}`
        : ''

    const calendarDaysMap = useMemo(() => {
        return new Map(
            calendarDays.map(day => [
                `${day.date.year}-${day.date.month}-${day.date.day}`,
                day,
            ])
        )
    }, [calendarDays])

    const getCalendarDayInfo = (date: Date) => {
        return calendarDaysMap.get(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`)
    }

    const getDayClassName = (calendarDay?: IShiftCalendarDay): string => {
        if(!calendarDay) return ''

        const classesList: string[] = []

        if(calendarDay.assignedTeamCount > 0) classesList.push(classes.hasInfo)
        if(calendarDay.hasReplacements) classesList.push(classes.hasReplacements)

        switch(calendarDay.status){
            case 'SHIFT_STATUS_PLANNED':
                classesList.push(classes.planned)
                break
            case 'SHIFT_STATUS_COMPLETED':
                classesList.push(classes.completed)
                break
            case 'SHIFT_STATUS_CANCELLED':
                classesList.push(classes.cancelled)
                break
        }

        return classesList.join(' ')
    }

    const getListInfo = (calendarDay?: IShiftCalendarDay) => {
        if(!calendarDay || (!calendarDay.assignedTeamCount && !calendarDay.hasReplacements)) return null

        return (
            <ul className={classes.infoList}>
                <li className={classes.item}>
                    <span className={classes.infoLabel}>Статус</span>
                    <span className={classes.infoCount}>{getStatusLabel(calendarDay.status)}</span>
                </li>
                <li className={classes.item}>
                    <span className={classes.infoLabel}>Звеньев</span>
                    <span className={classes.infoCount}>{calendarDay.assignedTeamCount}</span>
                </li>
                {calendarDay.hasReplacements && (
                    <li className={`${classes.item} ${classes.replacementInfo}`}>
                        <span className={classes.infoLabel}>Есть замены</span>
                        <span className={classes.infoCount}>да</span>
                    </li>
                )}
            </ul>
        )
    }

    const openDay = (date: Date) => {
        navigate(generatePath(CALENDAR_SHIFT_DAY_ROUTE.path, {date: getDateParam(date)}))
    }

    useEffect(() => {
        let isActual = true
        const firstDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), 1)
        const lastDay = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0)

        setIsLoading(true)
        setCalendarDays([])
        setPattern(undefined)
        setAvailableActions([])

        const getData = async() => {
            try{
                const response = await shiftService.getStaffShiftCalendar({
                    userId,
                    from: getLocalDateFromDate(firstDay),
                    to: getLocalDateFromDate(lastDay),
                })

                if(isActual){
                    setCalendarDays(response.days)
                    setPattern(response.pattern)
                    setAvailableActions(response.availableActions)
                }
            }
            catch(e){
                console.log(e)
                if(e instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else if(e instanceof MyError){
                    setGlobalMessage({message: e.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении персонального графика смен', type: 'error'})
                }
            }
            finally{
                if(isActual) setIsLoading(false)
            }
        }

        getData()

        return () => {
            isActual = false
        }
    }, [visibleMonth, userId, reloadIndex, setGlobalMessage, setIsAuth])

    const openEdit = () => {
        setIsEditOpen(true)
    }

    return (
        <section className={classes.container}>
            <section className={classes.header}>
                <section>
                    {!hideTitle && <h2 className={classes.title}>{title}</h2>}
                    <p className={classes.subtitle}>Смены сотрудника по датам. Время отображается по МСК.</p>
                </section>
                <nav className={classes.navigation} aria-label="Переключение месяца">
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() - 1))}
                        aria-label="Предыдущий месяц"
                    >
                        ‹
                    </button>
                    <button
                        type="button"
                        className={classes.navigationButton}
                        onClick={() => setVisibleMonth(new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1))}
                        aria-label="Следующий месяц"
                    >
                        ›
                    </button>
                </nav>
            </section>

            <section className={classes.patternPanel}>
                {pattern ? (
                    <>
                        <section className={classes.patternInfo}>
                            <span className={classes.patternBadge}>{pattern.workDays}/{pattern.restDays}</span>
                            <section>
                                <h3>График смен</h3>
                                <p>
                                    {patternTime}
                                    {' · '}
                                    действует с {getLocalDateLabel(pattern.effectiveFrom)}
                                    {pattern.effectiveTo ? ` по ${getLocalDateLabel(pattern.effectiveTo)}` : ' бессрочно'}
                                </p>
                            </section>
                        </section>
                        <section className={classes.patternMeta}>
                            <span>Опорная дата: {getLocalDateLabel(pattern.anchorDate)}</span>
                        </section>
                        {canUpdatePattern && (
                            <button type="button" className={classes.editButton} onClick={openEdit}>
                                Редактировать график
                            </button>
                        )}
                    </>
                ) : (
                    <section className={classes.emptyPattern}>
                        <h3>Постоянный график 2/2 не задан</h3>
                        <p>Пока показываем только смены, которые уже назначены сотруднику в календаре.</p>
                        {canUpdatePattern && (
                            <button type="button" className={classes.editButton} onClick={openEdit}>
                                Настроить график
                            </button>
                        )}
                    </section>
                )}
            </section>

            <section className={classes.monthTitle}>{monthTitle}</section>

            <section className={classes.calendarFrame}>
                {isLoading ? (
                    <section className={classes.loader}>
                        <LoaderSpinner />
                    </section>
                ) : (
                    <table className={classes.month}>
                        <thead>
                            <tr>
                                <th scope="col">Пн</th>
                                <th scope="col">Вт</th>
                                <th scope="col">Ср</th>
                                <th scope="col">Чт</th>
                                <th scope="col">Пт</th>
                                <th scope="col">Сб</th>
                                <th scope="col">Вс</th>
                            </tr>
                        </thead>
                        <tbody>
                            {weeks.map((week, weekIndex) => (
                                <tr key={weekIndex}>
                                    {week.map(day => {
                                        const isOtherMonth = day.class === 'opacity'
                                        const calendarDay = isOtherMonth ? undefined : getCalendarDayInfo(day.date)

                                        return (
                                            <td
                                                key={day.date.getTime()}
                                                className={classes.day +
                                                    (day.class === 'current' ? ` ${classes.current}` : '') +
                                                    (day.class === 'opacity' ? ` ${classes.opacity}` : '') +
                                                    (!isOtherMonth ? ` ${getDayClassName(calendarDay)}` : '')
                                                }
                                                tabIndex={isOtherMonth ? -1 : 0}
                                                onClick={isOtherMonth ? undefined : () => openDay(day.date)}
                                                onKeyDown={e => {
                                                    if(isOtherMonth) return
                                                    if(e.key === 'Enter' || e.key === ' '){
                                                        e.preventDefault()
                                                        openDay(day.date)
                                                    }
                                                }}
                                            >
                                                {!isOtherMonth && (
                                                    <>
                                                        <span className={classes.dayNumber}>{day.date.getDate()}</span>
                                                        {calendarDay?.assignedTeamCount ? (
                                                            <span className={classes.dayCaption}>
                                                                {calendarDay.assignedTeamCount} зв.
                                                            </span>
                                                        ) : null}
                                                        {calendarDay?.hasReplacements && (
                                                            <span className={classes.replacementBadge}>Замена</span>
                                                        )}
                                                        {getListInfo(calendarDay)}
                                                    </>
                                                )}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>

            <StaffShiftPatternEdit
                open={isEditOpen}
                setOpen={setIsEditOpen}
                userId={userId}
                pattern={pattern}
                onSuccess={nextPattern => {
                    setPattern(nextPattern)
                    setReloadIndex(current => current + 1)
                }}
            />
        </section>
    )
}
